import React, { useEffect, useState, useMemo } from "react";
import { motion } from "framer-motion";
import { ArrowsClockwise, TrendUp, ChartLineUp, Trophy, ArrowRight, ShieldCheck } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import SubscribeCard from "../components/SubscribeCard";
import DailySlate from "../components/DailySlate";
import TrialSignupModal from "../components/TrialSignupModal";
import TeamLogo from "../components/TeamLogo";
import { useAuth } from "../context/AuthContext";

const money = (n) => (n > 0 ? "+" : n < 0 ? "−" : "") + "$" + Math.abs(Number(n || 0)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const pct = (n) => (n == null ? "—" : `${(n * 100).toFixed(1)}%`);

function AnalystCard({ analyst, accent }) {
  const initial = analyst.name?.split(" ")?.[1]?.[0] || "?";
  const hot = analyst.win_rate >= 0.63;
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
      className={`relative bg-[#121212] border rounded-sm p-6 overflow-hidden ${
        accent === "charlie" ? "border-[#22C55E]/40" : "border-yellow-400/40"
      }`}
      data-testid={`analyst-${accent}`}
    >
      <div className={`absolute -right-12 -top-12 w-44 h-44 rounded-full blur-3xl opacity-20 ${
        accent === "charlie" ? "bg-[#22C55E]" : "bg-yellow-400"
      }`} />
      <div className="relative flex items-center gap-4 mb-5">
        <div className={`w-14 h-14 rounded-sm flex items-center justify-center font-display text-3xl ${
          accent === "charlie" ? "bg-[#22C55E] text-black" : "bg-yellow-400 text-black"
        }`}>
          {initial}
        </div>
        <div>
          <div className="font-display text-2xl tracking-wider">{analyst.name}</div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">{analyst.tagline}</div>
        </div>
        {hot && (
          <span className="ml-auto text-[10px] uppercase tracking-widest text-[#22C55E] border border-[#22C55E]/40 px-2 py-0.5 rounded-sm">Hot</span>
        )}
      </div>
      <div className="grid grid-cols-3 gap-3">
        <div className="border border-white/10 rounded-sm p-3">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">Picks YTD</div>
          <div className="font-display text-3xl mt-0.5">{analyst.bets}</div>
        </div>
        <div className="border border-white/10 rounded-sm p-3">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">Record</div>
          <div className="font-display text-3xl mt-0.5">{analyst.wins}-{analyst.losses}</div>
        </div>
        <div className="border border-white/10 rounded-sm p-3">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">Hit Rate</div>
          <div className={`font-display text-3xl mt-0.5 ${hot ? "text-[#22C55E]" : "text-zinc-200"}`} data-testid={`analyst-${accent}-hit-rate`}>{pct(analyst.win_rate)}</div>
        </div>
      </div>

      {/* Per-team accuracy within this strategy */}
      {Array.isArray(analyst.team_breakdown) && analyst.team_breakdown.length > 0 && (
        <div className="mt-4 border-t border-white/5 pt-4" data-testid={`analyst-${accent}-team-breakdown`}>
          <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-2">Hit rate by team</div>
          <div className="space-y-1.5">
            {analyst.team_breakdown.map((row) => {
              const tone = row.win_rate >= 0.6 ? "text-[#22C55E]" : row.win_rate >= 0.5 ? "text-zinc-200" : "text-[#FF3B30]";
              return (
                <div key={row.team_id} className="flex items-center gap-3 text-xs font-mono-d" data-testid={`team-breakdown-${accent}-${row.team_abbr}`}>
                  <TeamLogo sport="mlb" abbr={row.team_abbr} size={20} />
                  <span className="w-12 text-zinc-400 uppercase tracking-wider">{row.team_abbr}</span>
                  <span className="flex-1 text-zinc-300 truncate">{row.team}</span>
                  <span className="w-12 text-right text-zinc-500">{row.wins}-{row.losses}</span>
                  <span className={`w-14 text-right font-bold ${tone}`}>{pct(row.win_rate)}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </motion.div>
  );
}

export default function PublicPicks() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [trialOpen, setTrialOpen] = useState(false);
  const [userLadder, setUserLadder] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/picks/feed");
      setData(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  // Pull the user's saved stake ladder so each pick card can render their
  // personalized stake instead of the generic $425 base.
  useEffect(() => {
    if (!user) { setUserLadder(null); return; }
    let cancel = false;
    api.get("/account/stake-ladder").then((res) => {
      if (!cancel) setUserLadder(res.data?.ladder || null);
    }).catch(() => { if (!cancel) setUserLadder(null); });
    return () => { cancel = true; };
  }, [user]);

  const isAdmin = !!(user && user.role === "admin");
  const trialActive = !!(data && data.trial_active);
  const fullAccess = isAdmin || trialActive;
  const lastWeek = useMemo(() => {
    if (!data?.weekly_summary?.length) return null;
    return [...data.weekly_summary].reverse()[0];
  }, [data]);

  return (
    <div className="max-w-[1400px] mx-auto px-6 py-10">
      {/* Hero */}
      <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#007AFF] mb-1">MLB · Analyst Track Record</div>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.95]">
            TWO ANALYSTS.<br/>
            <span className="text-[#007AFF]">ONE EDGE.</span>
          </h1>
          <p className="text-sm text-zinc-400 mt-4 max-w-2xl leading-relaxed">
            Charlie hunts bounce-back patterns. Dakota rides momentum continuation.
            Their predictions are public — their math is private. Here&apos;s the receipt.
          </p>
          <div className="flex items-center gap-3 mt-5 flex-wrap">
            {!fullAccess && (
              <Link
                to="/signup"
                data-testid="hero-signup-cta"
                className="inline-flex items-center gap-1.5 px-5 py-2.5 bg-[#22C55E] hover:bg-[#16A34A] text-black text-xs uppercase tracking-[0.25em] font-bold rounded-sm transition-colors"
              >
                Start 7-Day Free Trial →
              </Link>
            )}
            <Link
              to="/receipts"
              data-testid="link-receipts-hero"
              className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.25em] text-[#22C55E] hover:text-white border-b border-[#22C55E]/40 hover:border-white/40 pb-0.5 transition-colors"
            >
              📜 See the receipts →
            </Link>
          </div>
        </div>
        <button
          onClick={load}
          disabled={loading}
          data-testid="picks-refresh"
          className="flex items-center gap-2 px-5 py-2.5 border border-white/20 hover:border-white/60 rounded-sm text-xs uppercase tracking-wider font-bold transition-all"
        >
          <ArrowsClockwise size={16} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      {!data ? (
        <div className="text-zinc-500 font-mono-d" data-testid="picks-loading">Loading analyst track record…</div>
      ) : (
        <>
          {/* For admins and active trial users: DAILY SLATE is the FIRST thing shown — most actionable */}
          {fullAccess && (
            <DailySlate settledPicks={data.picks || []} upcomingPicks={data.upcoming_picks || []} userLadder={userLadder} />
          )}

          {/* Trial countdown banner — visible only during active trial */}
          {trialActive && !isAdmin && (
            <div className="mb-8 bg-[#0d1a2b] border border-[#007AFF]/30 rounded-sm px-5 py-3 flex items-center justify-between flex-wrap gap-3" data-testid="trial-banner">
              <div className="text-xs">
                <span className="text-[#007AFF] font-bold uppercase tracking-widest text-[10px]">Trial Active</span>
                <span className="ml-3 text-zinc-300">Full pick access until your trial ends. Enjoy.</span>
              </div>
            </div>
          )}

          {/* Combined headline stat strip */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-6">
            <HeadlineStat
              label="Total Picks (YTD)"
              value={data.analysts.charlie.bets + data.analysts.dakota.bets}
              testid="stat-total-picks"
            />
            <HeadlineStat
              label="Combined Record"
              value={`${data.analysts.charlie.wins + data.analysts.dakota.wins}-${data.analysts.charlie.losses + data.analysts.dakota.losses}`}
              testid="stat-combined-record"
            />
            <HeadlineStat
              label="Combined Hit Rate"
              value={pct(
                (data.analysts.charlie.bets + data.analysts.dakota.bets) > 0
                  ? (data.analysts.charlie.wins + data.analysts.dakota.wins) /
                    (data.analysts.charlie.bets + data.analysts.dakota.bets)
                  : 0
              )}
              highlight
              testid="stat-combined-wr"
            />
          </div>

          {/* Tamper-evident trust strip: every pick is locked into an immutable
              ledger BEFORE first pitch. Subscribers can audit the full ledger
              via /receipts. */}
          <Link
            to="/receipts"
            data-testid="trust-strip-receipts"
            className="group relative block mb-10 overflow-hidden bg-gradient-to-r from-[#0a1a14] via-[#0a0a0a] to-[#0a141a] border border-[#22C55E]/25 hover:border-[#22C55E]/60 rounded-sm transition-colors"
          >
            <div className="absolute inset-y-0 left-0 w-1 bg-[#22C55E]" />
            <div className="flex items-center gap-4 px-5 py-3.5 sm:px-6 sm:py-4">
              <div className="w-9 h-9 rounded-sm bg-[#22C55E]/15 border border-[#22C55E]/40 flex items-center justify-center shrink-0">
                <ShieldCheck size={18} weight="fill" className="text-[#22C55E]" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#22C55E] mb-0.5">Tamper-Evident Track Record</div>
                <div className="text-xs sm:text-sm text-zinc-300 leading-snug">
                  Every pick is timestamped and locked into an immutable ledger
                  <span className="hidden sm:inline"> <span className="text-white font-semibold">before first pitch</span> — no edits, no deletions, no cherry-picking.</span>
                  <span className="sm:hidden text-white font-semibold"> before first pitch.</span>
                </div>
              </div>
              <div className="shrink-0 text-[10px] uppercase tracking-[0.25em] text-zinc-400 group-hover:text-white inline-flex items-center gap-1.5 transition-colors">
                Audit&nbsp;the&nbsp;ledger <ArrowRight size={12} className="transition-transform group-hover:translate-x-0.5" />
              </div>
            </div>
          </Link>

          {/* Analyst cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-10">
            <AnalystCard analyst={data.analysts.charlie} accent="charlie" />
            <AnalystCard analyst={data.analysts.dakota} accent="dakota" />
          </div>

          {/* Anonymous users: subscribe gate + free-trial CTA. Admins/trial already got DailySlate. */}
          {!fullAccess && (
            <div className="mb-12">
              <div className="bg-gradient-to-br from-[#0d1a2b] via-[#0a0a0a] to-[#1a0d0d] border border-[#007AFF]/30 rounded-sm p-8 md:p-10 mb-5 text-center" data-testid="trial-cta-card">
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#007AFF] mb-2">Limited offer</div>
                <h2 className="font-display text-4xl sm:text-5xl mb-3">
                  GET 7 DAYS <span className="text-[#007AFF]">FREE.</span>
                </h2>
                <p className="text-sm text-zinc-400 max-w-xl mx-auto mb-5">
                  Full access to today&apos;s picks, tomorrow&apos;s picks, and the live DraftKings moneyline.
                  Card on file — not charged for 7 days, then $15/week. Cancel anytime.
                </p>
                <div className="flex items-center justify-center gap-3 flex-wrap">
                  <Link
                    to="/signup"
                    data-testid="open-signup-cta"
                    className="inline-block px-8 py-3 bg-[#22C55E] hover:bg-[#16A34A] text-black text-xs uppercase tracking-[0.25em] font-bold rounded-sm transition-colors"
                  >
                    Start Free Trial →
                  </Link>
                  <Link
                    to="/subscribe"
                    data-testid="open-subscribe-plans"
                    className="inline-block px-8 py-3 border border-white/30 hover:border-white/60 text-white text-xs uppercase tracking-[0.25em] font-bold rounded-sm transition-colors"
                  >
                    Compare plans
                  </Link>
                </div>
              </div>
              <SubscribeCard upcomingCount={data.upcoming_count || 0} />
            </div>
          )}
          <TrialSignupModal open={trialOpen} onClose={() => setTrialOpen(false)} />

          {/* Last week snapshot + history teaser */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-10">
            {lastWeek && (
              <div className="bg-[#121212] border border-white/10 rounded-sm p-5 lg:col-span-1" data-testid="last-week-card">
                <div className="flex items-baseline justify-between mb-3">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500">Last week (Mon→Sun)</div>
                    <div className="font-mono-d text-xs text-zinc-300">{lastWeek.week_start}</div>
                  </div>
                  <Trophy size={20} className="text-yellow-400" />
                </div>
                <div className="space-y-2 text-xs">
                  <Row label="Charlie" bets={lastWeek.charlie_bets} wins={lastWeek.charlie_wins} losses={lastWeek.charlie_losses} pnl={lastWeek.charlie_pnl} showMoney={isAdmin} />
                  <Row label="Dakota" bets={lastWeek.dakota_bets} wins={lastWeek.dakota_wins} losses={lastWeek.dakota_losses} pnl={lastWeek.dakota_pnl} showMoney={isAdmin} />
                  <div className="border-t border-white/10 pt-2 mt-2">
                    <Row label="Combined" bets={lastWeek.combined_bets} wins={lastWeek.combined_wins} losses={lastWeek.combined_losses} pnl={lastWeek.combined_pnl} bold showMoney={isAdmin} />
                  </div>
                </div>
                <Link to="/weekly" data-testid="link-weekly-from-landing"
                  className="mt-4 inline-flex items-center gap-1 text-[11px] uppercase tracking-widest text-[#007AFF] hover:underline">
                  Full weekly log <ArrowRight size={12} />
                </Link>
              </div>
            )}

            <div className="bg-[#121212] border border-white/10 rounded-sm p-5 lg:col-span-2" data-testid="recent-results-card">
              <div className="flex items-baseline justify-between mb-3">
                <div className="flex items-center gap-2">
                  <ChartLineUp size={18} className="text-[#007AFF]" />
                  <h3 className="font-display text-2xl tracking-wider">Recent Settled Picks</h3>
                </div>
                <Link to="/history" data-testid="link-history-from-landing"
                  className="text-[11px] uppercase tracking-widest text-[#007AFF] hover:underline flex items-center gap-1">
                  Full history <ArrowRight size={12} />
                </Link>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-[10px] uppercase tracking-widest text-zinc-500 border-b border-white/10">
                    <tr>
                      <th className="text-left py-2 pr-3">Date</th>
                      <th className="text-left py-2 pr-3">Analyst</th>
                      <th className="text-left py-2 pr-3">Result</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...data.picks].reverse().slice(0, 8).map((p, i) => (
                      <tr key={i} className="border-b border-white/5" data-testid="recent-pick-row">
                        <td className="py-1.5 pr-3 font-mono-d text-xs text-zinc-300">{p.date}</td>
                        <td className="py-1.5 pr-3">
                          <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm ${
                            p.analyst === "Charlie" ? "bg-[#22C55E]/20 text-[#22C55E] border border-[#22C55E]/40"
                            : "bg-yellow-400/20 text-yellow-400 border border-yellow-400/40"
                          }`}>{p.analyst}</span>
                        </td>
                        <td className="py-1.5 pr-3">
                          {p.result === "W" ? (
                            <span className="bg-[#22C55E] text-black font-bold text-xs px-2 py-0.5 rounded-sm">W</span>
                          ) : p.result === "L" ? (
                            <span className="bg-[#FF3B30] text-black font-bold text-xs px-2 py-0.5 rounded-sm">L</span>
                          ) : (
                            <span className="text-[10px] uppercase text-zinc-500">{p.result}</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-[10px] text-zinc-500 mt-3 italic">
                Team names hidden on the landing. Full pick details in <Link to="/history" className="text-[#007AFF] hover:underline">history</Link>.
              </p>
            </div>
          </div>

          {/* Bottom-of-page conversion CTA — only for anonymous visitors who scrolled all the way down */}
          {!fullAccess && (
            <div className="mb-12 bg-gradient-to-r from-[#0d1a2b] via-[#0a0a0a] to-[#0d1a2b] border border-[#22C55E]/40 rounded-sm p-8 md:p-10 text-center" data-testid="bottom-cta-card">
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#22C55E] mb-2">Saw enough?</div>
              <h2 className="font-display text-4xl sm:text-5xl mb-3 leading-tight">
                STOP READING.<br/>
                <span className="text-[#22C55E]">START WINNING.</span>
              </h2>
              <p className="text-sm text-zinc-400 max-w-xl mx-auto mb-5">
                Tomorrow&apos;s picks drop tonight. Lock in your access in under 60 seconds.
              </p>
              <Link
                to="/signup"
                data-testid="bottom-signup-cta"
                className="inline-block px-10 py-3.5 bg-[#22C55E] hover:bg-[#16A34A] text-black text-sm uppercase tracking-[0.25em] font-bold rounded-sm transition-colors"
              >
                Start 7-Day Free Trial →
              </Link>
              <div className="mt-3 text-[10px] uppercase tracking-widest text-zinc-500">
                $15/week after trial · Cancel anytime · 21+
              </div>
            </div>
          )}

          {/* Stake sizing explainer — admin-only since it exposes $ tiers */}
          {isAdmin && data.tiers_explanation && (
          <section className="bg-[#121212] border border-white/10 rounded-sm p-5" data-testid="tiers-section">
            <div className="flex items-baseline gap-3 mb-3">
              <TrendUp size={18} className="text-[#007AFF]" />
              <h3 className="font-display text-2xl tracking-wider">{data.tiers_explanation.title}</h3>
            </div>
            <p className="text-xs text-zinc-400 max-w-3xl mb-4">
              Stake amounts scale dynamically with each week&apos;s performance. Every Monday resets the clock.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              {data.tiers_explanation.tiers.map((t, i) => (
                <div key={i} className={`border rounded-sm p-3 ${
                  t.amount === -750 ? "border-[#FF3B30]/40 bg-[#FF3B30]/5"
                  : t.amount === 1500 ? "border-[#22C55E]/40 bg-[#22C55E]/5"
                  : "border-white/10 bg-[#0A0A0A]"
                }`}>
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500">{t.label}</div>
                  <div className={`font-display text-2xl mt-1 ${
                    t.amount > 0 ? "text-white" : t.amount < 0 ? "text-[#FF3B30]" : "text-zinc-400"
                  }`}>
                    {t.amount === -750 ? "−$750" : t.amount === 1500 ? "+$1,500" : `$${t.amount}`}
                  </div>
                  <div className="text-[10px] text-zinc-500 mt-1">{t.note}</div>
                </div>
              ))}
            </div>
          </section>
          )}
        </>
      )}
    </div>
  );
}

function HeadlineStat({ label, value, highlight, tone, testid }) {
  const toneClass = tone === "good" ? "text-[#22C55E]" : tone === "bad" ? "text-[#FF3B30]" : "text-white";
  return (
    <div className={`border rounded-sm p-4 ${highlight ? "border-[#007AFF]/40 bg-[#007AFF]/5" : "border-white/10 bg-[#121212]"}`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className={`font-display text-4xl mt-1 ${toneClass}`}>{value}</div>
    </div>
  );
}

function Row({ label, bets, pnl, bold, showMoney }) {
  return (
    <div className={`flex items-center justify-between ${bold ? "font-bold" : ""}`}>
      <span className="text-zinc-400">{label}</span>
      <span className={`font-mono-d text-zinc-500 ${showMoney ? "mr-3" : ""}`}>{bets} bets</span>
      {showMoney && (
        <span className={`font-mono-d ${pnl > 0 ? "text-[#22C55E]" : pnl < 0 ? "text-[#FF3B30]" : "text-zinc-400"}`}>{money(pnl)}</span>
      )}
    </div>
  );
}

function AdminTeaser_REMOVED() { return null; /* dead code: previously rendered the "X plays queued" teaser; replaced by DailySlate */ }
